import React, { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowDown, MessageCircle, Shield } from 'lucide-react';

const HeroSection = () => {
  const containerRef = useRef(null);
  const [mousePosition, setMousePosition] = useState({ x: 0.5, y: 0.5 });
  
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });

  const imageY = useTransform(scrollYProgress, [0, 1], ['0%', '20%']);
  const textY = useTransform(scrollYProgress, [0, 1], ['0%', '40%']);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      const x = e.clientX / window.innerWidth;
      const y = e.clientY / window.innerHeight;
      setMousePosition({ x, y });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Line-by-line stagger animation
  const titleLines = ["Brothel Jobs", "Melbourne & Sydney"];
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.3,
      }
    }
  };

  const lineVariants = {
    hidden: { 
      clipPath: "inset(0 100% 0 0)",
      opacity: 0 
    },
    visible: { 
      clipPath: "inset(0 0% 0 0)",
      opacity: 1,
      transition: {
        duration: 1.2,
        ease: [0.77, 0, 0.175, 1]
      }
    }
  };

  const scrollToContent = () => {
    const element = document.querySelector('#about');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section 
      ref={containerRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      data-testid="hero-section"
      id="about"
    >
      {/* Background Image with Parallax */}
      <motion.div 
        className="absolute inset-0 z-0"
        style={{ y: imageY }}
      >
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://sydneybrothelsguide.com.au/images/stiletto-royal-suite.jpg')`,
          }}
        />
        {/* Gradient overlay */}
        <div className="absolute inset-0 hero-gradient" />
        
        {/* Interactive spotlight */}
        <div 
          className="absolute inset-0 opacity-60 transition-all duration-300"
          style={{
            background: `radial-gradient(ellipse 800px 600px at ${mousePosition.x * 100}% ${mousePosition.y * 100}%, rgba(224, 40, 82, 0.15), transparent 70%)`
          }}
        />
      </motion.div>

      {/* Content */}
      <motion.div 
        className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pt-32 pb-20"
        style={{ y: textY, opacity }}
      >
        <div className="text-left max-w-4xl">


          {/* Main Title with line-by-line reveal */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="mb-6"
          >
            {titleLines.map((line, index) => (
              <motion.h1
                key={index}
                variants={lineVariants}
                className={`font-serif font-light tracking-tight leading-[1.05] ${
                  index === 0 
                    ? 'text-4xl sm:text-6xl lg:text-7xl text-[#F7F6F2]' 
                    : 'text-4xl sm:text-6xl lg:text-7xl text-[#E02852]'
                }`}
                data-testid={`hero-title-line-${index}`}
              >
                {line}
              </motion.h1>
            ))}
          </motion.div>

          {/* Earnings Badge - Attention Grabbing */}
          <motion.div
            className="mb-8"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6, duration: 0.5, ease: [0.23, 1, 0.32, 1] }}
          >
            <div 
              className="inline-flex items-center gap-4 px-6 py-4 rounded-2xl border-2 border-[#D4AF37] bg-[rgba(212,175,55,0.1)]"
              data-testid="earnings-badge"
            >
              <div className="flex flex-col">
                <span className="font-mono text-xs text-[#D4AF37] tracking-widest uppercase">Earn Up To</span>
                <span className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#D4AF37] tracking-tight">
                  $3,000–$6,000
                </span>
                <span className="font-mono text-xs text-[#D4AF37] tracking-widest uppercase">Per Week</span>
              </div>
              <div className="hidden sm:block w-px h-16 bg-[#D4AF37] opacity-30" />
              <div className="hidden sm:flex flex-col text-left">
                <span className="text-sm text-[#F7F6F2] font-medium">Highest Industry Rates</span>
                <span className="text-xs text-[#A1A1AA]">No experience required</span>
              </div>
            </div>
          </motion.div>

          {/* Subtitle */}
          <motion.p
            className="text-lg sm:text-xl text-[#A1A1AA] max-w-2xl mb-10 leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.6 }}
            data-testid="hero-subtitle"
          >
            Apply now for exclusive positions at Melbourne's premier 5-star establishment. 
            Female management, full training provided, flexible scheduling, and the highest 
            earning potential in the industry. Your privacy and comfort are our priority.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            className="flex flex-col sm:flex-row gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.6 }}
          >
            <a
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="btn-primary px-8 py-4 rounded-full text-base font-medium flex items-center justify-center gap-2 magnetic-btn"
              data-testid="hero-apply-btn"
            >
              <span>Apply Confidentially</span>
              <ArrowDown size={18} />
            </a>
            <a
              href="sms:0429085683?body=Hi%20Lisa,%20I'm%20interested%20in%20confidential%20employment%20opportunities%20in%20Melbourne."
              className="btn-secondary px-8 py-4 rounded-full text-base font-medium flex items-center justify-center gap-2"
              data-testid="hero-sms-btn"
            >
              <MessageCircle size={18} />
              <span>SMS Lisa: 0429 085683</span>
            </a>
          </motion.div>

          {/* Stats Row */}
          <motion.div
            className="grid grid-cols-3 gap-8 mt-16 pt-10 border-t border-[rgba(255,255,255,0.08)]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.6 }}
          >
            {[
              { number: '100%', label: 'Confidential' },
              { number: 'Flexible', label: 'Scheduling' },
              { number: 'Safe', label: 'Regulated' },
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                className="text-left"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.3 + index * 0.1, duration: 0.4 }}
                data-testid={`hero-stat-${index}`}
              >
                <div className="stat-number text-2xl sm:text-3xl lg:text-4xl">{stat.number}</div>
                <div className="stat-label mt-1">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.button
        onClick={scrollToContent}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2 text-[#A1A1AA] hover:text-[#F7F6F2] transition-colors cursor-pointer"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 0.6 }}
        data-testid="scroll-indicator"
      >
        <span className="font-mono text-xs tracking-widest uppercase">Scroll</span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
        >
          <ArrowDown size={20} />
        </motion.div>
      </motion.button>
    </section>
  );
};

export default HeroSection;
